<template>
  <base-spinner v-if="loading" />
  <div v-else>
    <div class="p-4">
      <div class="trip-detail-hide-on-transfer-print trip-detail-screen-content">
      <b-alert :show="error && error.length ? true : false" variant="danger">
        <div v-if="Array.isArray(error)">
          <div class="d-flex flex-column my-1" v-for="(error_text, index) in error" :key="index">
            {{ error_text }}
          </div>
        </div>
        <div v-else>
          <div v-html="error || '&nbsp;'"></div>
        </div>
      </b-alert>
      <div class="row align-items-center mb-2">
        <div class="col-lg-4 col-md-6 col-xs-12">
          <div v-if="isReservationTrip ? data?.reservation?.client?._id : data?.contract?.client?._id"
            class="header-block d-flex align-items-center">
            <div class="icon-container">
              <img src="@/assets/images/icons/user-profile.svg" alt="user-profile" />
            </div>
            <div style="margin-inline: 0.5rem">{{ $t("client") }}</div>
            <div class="bold-text">
              {{ clientName }}
            </div>
          </div>
        </div>
        <div class="col-lg-4 col-md-6 col-xs-12">
          <div v-if="data?.reservation?.category?._id" class="header-block d-flex align-items-center">
            <div class="icon-container">
              <img src="@/assets/images/icons/column-vertical.svg" alt="column-vertical" />
            </div>
            <div style="margin-inline: 0.5rem">
              {{ $t("reservation-category") }}
            </div>
            <div class="bold-text">
              {{
                isLangRtl
                  ? data?.reservation?.category?.arabicName
                  : data?.reservation?.category?.name
              }}
            </div>
          </div>
        </div>
      </div>
      <div class="row align-items-start mt-2">
        <div class="col-lg-4 col-md-6 col-xs-12">
          <div v-if="data?.service?._id" class="header-block d-flex align-items-center">
            <div class="icon-container">
              <img src="@/assets/images/icons/column-vertical.svg" alt="column-vertical" />
            </div>
            <div style="margin-inline: 0.5rem">
              {{ $t("service") }}
            </div>
            <div class="bold-text">
              {{ isLangRtl ? data?.service?.arabicName : data?.service?.name }}
            </div>
          </div>
        </div>
        <div class="col-lg-4 col-md-6 col-xs-12">
          <div v-if="data?.service?.category?._id" class="header-block d-flex align-items-center">
            <div class="icon-container">
              <img src="@/assets/images/icons/column-vertical.svg" alt="column-vertical" />
            </div>
            <div style="margin-inline: 0.5rem">
              {{ $t("service-category") }}
            </div>
            <div class="bold-text">
              {{
                isLangRtl
                  ? data?.service?.category?.arabicName
                  : data?.service?.category?.name
              }}
            </div>
          </div>
        </div>
        <div :class="[
          isLangRtl ? 'align-items-start' : 'align-items-end',
          'col-lg-4',
          'd-flex',
          'flex-column',
          'gap-2',
        ]">
          <ChangeTripStatus v-if="data?.status" :isMainDriverAssigned="data?.mainDriver?._id" :status="data.status" :_id="data._id"
            @getData="getData" :serviceAllowance="tripServiceAllowanceAmount" :tripData="data" :tripType="tripType"
            :allowanceTypeGroup="allowanceTypeGroup" />
          <base-button v-if="showInspectionPrintButton" variant="secondary" id="download-details-btn" :loading="pdfLoading" @click="generateInspectionPDF">
            {{ $t("print-inspection-list") }}
            <img :src="require(`@/assets/images/printer.png`)" alt="image-placeholder" class="cursor-pointer ms-2" />
          </base-button>
        </div>
      </div>
      <div class="row align-items-center mb-2 mt-2" v-if="tripService?.description">
        <div class="col-lg-8 col-md-6 col-xs-12">
          <div class="header-block d-flex align-items-center">
            <div class="icon-container">
              <img src="@/assets/images/icons/column-vertical.svg" alt="column-vertical" />
            </div>
            <div style="margin-inline: 0.5rem">{{ $t("description") }}</div>
            <div class="bold-text d-flex justify-content-end">
              {{ tripService?.description }}
            </div>
          </div>
        </div>
      </div>
      <hr />
      <div v-if="data?.subStatus === 'Reviewed'"
        class="info-block d-flex align-items-center justify-content-between mb-3" style="background: var(--color-121)">
        <div class="d-flex align-items-center">
          <div class="icon me-2" style="background: var(--color-92)">
            <img :src="require(`@/assets/images/icons/arrow-refresh-icon.svg`)" alt="arrow-refresh-icon" />
          </div>
          <div>
            {{ $t("reviewed-message", { id: data?._id }) }}
          </div>
        </div>
        <div v-if="!$store.getters['enums/is_not_permitted']('markAsUnRead')">
          <base-button variant="info" :loading="action_loading" @click="unview">
            {{ $t("mark-as-unread") }}
          </base-button>
        </div>
      </div>
      <div v-else-if="data?.subStatus === 'Approval-Needed'"
        class="info-block d-flex align-items-center justify-content-between mb-3" style="background: var(--color-121)">
        <div class="d-flex align-items-center">
          <div class="icon me-2" style="background: var(--color-92)">
            <img :src="require(`@/assets/images/icons/arrow-refresh-icon.svg`)" alt="arrow-refresh-icon" />
          </div>
          <div>
            {{ $t("approval-needed-message", { id: data?._id }) }}
          </div>
        </div>
        <div v-if="!$store.getters['enums/is_not_permitted']('markAsRead')">
          <base-button variant="info" :loading="action_loading" @click="viewed">
            {{ $t("mark-as-read") }}
          </base-button>
        </div>
      </div>
      <div v-else-if="data?.subStatus === 'Action-Required'"
        class="info-block d-flex align-items-center justify-content-between mb-3" style="background: var(--color-123)">
        <div class="d-flex align-items-center">
          <div class="icon me-2" style="background: var(--color-124)">
            <img :src="require(`@/assets/images/icons/alert-triangle-icon.svg`)" alt="alert-triangle-icon" />
          </div>
          <div v-if="checkTime">
            {{ $t("action-required-message-more-than-24", { id: data?._id }) }}
          </div>
          <div v-else>
            {{ $t("action-required-message-less-than-24", { id: data?._id }) }}
          </div>
        </div>
        <div v-if="!$store.getters['enums/is_not_permitted']('markAsRead')">
          <base-button variant="info" :loading="action_loading" @click="viewed">
            {{ $t("mark-as-read") }}
          </base-button>
        </div>
      </div>
      <div v-else-if="data?.status === 'Trip-Started'"
        class="info-block d-flex align-items-center justify-content-between mb-3" style="background: var(--color-121)">
        {{ $t("trip-started-toast") }}
      </div>
      <div v-else-if="data?.status === 'Trip-Arrived-At-Pickup-Location'"
        class="info-block d-flex align-items-center justify-content-between mb-3" style="background: var(--color-121)">
        {{ $t("trip-arrived-at-pickup-location-toast") }}
      </div>
      <div v-else-if="data?.status === 'Trip-Passenger-Picked-Up'"
        class="info-block d-flex align-items-center justify-content-between mb-3" style="background: var(--color-121)">
        {{ $t("trip-passenger-picked-up-toast") }}
      </div>
      <div v-else-if="data?.status === 'Trip-Arrived-To-Dropped-Off'"
        class="info-block d-flex align-items-center justify-content-between mb-3" style="background: var(--color-121)">
        {{ $t("trip-arrived-to-dropped-off-toast") }}
      </div>
      <div v-else-if="data?.status === 'Trip-Passenger-Dropped-Off'"
        class="info-block d-flex align-items-center justify-content-between mb-3" style="background: var(--color-121)">
        {{ $t("trip-passenger-dropped-off-toast") }}
      </div>
      <div v-else-if="
        data?.status === 'Trip-Completed' ||
        data?.status === 'Trip-Completed-Note'
      " class="info-block d-flex align-items-center justify-content-between mb-3"
        style="background: var(--color-121)">
        {{ $t("trip-completed-toast") }}
      </div>
      <div v-if="false" class="info-block d-flex align-items-center justify-content-between mb-3"
        style="background: var(--color-126)">
        <div class="d-flex align-items-center">
          <div class="icon me-2" style="background: var(--color-127)">
            <img :src="require(`@/assets/images/icons/clock-2-icon.svg`)" alt="clock-icon" />
          </div>
          <div>
            Trip #id has been newly created. Please review and approve the
            Auto-assign.
          </div>
        </div>
        <div>
          <base-button variant="secondary2">{{ $t("approve") }}</base-button>
        </div>
      </div>
      <div class="row align-items-center mb-3">
        <div class="col-lg-2 col-md-6 col-xs-12" v-if="data?.reservation?._id">
          <span style="color: var(--color-66)">{{ $t("reservation-id") }} :
          </span>
          <span class="bold-text">{{ data?.reservation?._id }}</span>
        </div>
        <div class="col-lg-2 col-md-6 col-xs-12" v-if="data?.contract">
          <span style="color: var(--color-66)">{{ $t("contract-id") }} :
          </span>
          <span class="bold-text">{{ data?.contract?._id }}</span>
        </div>
        <div class="col-lg-2 col-md-6 col-xs-12" v-if="data?.route?._id">
          <span style="color: var(--color-66)">
            {{ $t("route") }} :
          </span>
          <span class="bold-text">
            {{
              isLangRtl
                ? data?.route?.arabicName
                : data?.route?.name
            }}
          </span>
        </div>
        <div class="col-lg-2 col-md-6 col-xs-12" v-if="data?._id">
          <span style="color: var(--color-66)">{{ $t("trip-id") }} : </span>
          <span class="bold-text text-capitalize">{{ data?._id }}</span>
        </div>
        <div class="col-lg-2 col-md-6 col-xs-12 ms-auto" v-if="data?.reservation?._id && hasPermissionPrintSalesOrder && !data?.isDeadhead">
          <base-button variant="secondary" id="download-details-btn" :loading="pdfLoading" @click="generatePDF">
            {{ $t("print") }}
            <img :src="require(`@/assets/images/printer.png`)" alt="image-placeholder" class="cursor-pointer ms-2" />
          </base-button>
        </div>
        <div
          class="col-lg-4 col-md-12 col-xs-12 ms-auto d-flex align-items-start justify-content-end gap-2 flex-wrap"
          v-if="showMapButtonVisible"
        >
          <div class="show-map-wrapper" style="position: relative">
            <base-button variant="primary" @click="toggleShowMap">
              <span class="showMap">
                {{ $t("show-map") }}
              </span>
            </base-button>
            <div class="show-map-options" v-if="showMapSelection" v-click-outside="toggleShowMap">
              <div class="option" @click="showActualRouteModal('third-party')">
                {{ $t("show-bus-route") }}
              </div>
              <div class="option" @click="showActualRouteModal('mobile')">
                {{ $t("show-mobile-route") }}
              </div>
              <div class="option" @click="showActualRouteModal('both')">
                {{ $t("show-combined-route") }}
              </div>
            </div>
          </div>
        </div>
        <div class="col-lg-3 col-md-6 col-xs-12 ms-auto" v-if="showCompleteWithNote">
          <base-button variant="secondary" @click="$bvModal.show('complete-with-note-modal')">
            {{ $t("complete-with-note") }}
          </base-button>
        </div>
      </div>
      <div class="trip-place-container mb-3">
        <div class="d-flex align-items-center justify-content-between mb-3">
          <div class="d-flex align-items-center">
            <div class="icon-container">
              <img src="@/assets/images/icons/Group.svg" alt="Group" />
            </div>
            <div style="margin-inline-start: 0.5rem">
              {{ $t("cities-and-address") }}
            </div>
          </div>
          <div v-if="allowEdit || showCancelButton || isHajjGarageTransferTrip" class="d-flex align-items-center gap-2 flex-wrap">
            <base-button
              v-if="isHajjGarageTransferTrip"
              variant="secondary"
              type="button"
              id="hajj-garage-transfer-print-detail-btn"
              :loading="garageTransferPrintLoading"
              :disabled="garageTransferPrintLoading || garageTransferTripTablePrintLoading"
              @click="printGarageTransferOrder"
            >
              {{ $t("hajj-garage-transfer-print-order") }}
            </base-button>
            <!-- <base-button
              v-if="isHajjGarageTransferTrip"
              variant="secondary"
              type="button"
              id="hajj-garage-transfer-print-trip-table-btn"
              :loading="garageTransferTripTablePrintLoading"
              :disabled="garageTransferTripTablePrintLoading || garageTransferPrintLoading"
              @click="printGarageTransferTripTable"
            >
              {{ $t("hajj-garage-transfer-print-trip-table") }}
            </base-button> -->
            <div v-if="allowEdit">
              <base-button variant="info" @click="moveToEdit(data?._id)">
                {{ $t("edit-journey") }}
              </base-button>
            </div>
            <div v-if="showCancelButton">
              <base-button variant="danger" @click="showCancelTripModel">
                {{ $t("cancel-trip") }}
              </base-button>
            </div>
          </div>
        </div>
        <div class="d-flex justify-content-between mb-4">
          <div v-if="data?.service?.startCity?._id && !isHajjGarageTransferTrip">
            <div>{{ $t("from-city") }}</div>
            <div class="bold-text">
              {{
                isLangRtl
                  ? data?.service?.startCity?.arabicName
                  : data?.service?.startCity?.name
              }}
            </div>
          </div>
          <div v-if="data?.service?.endCity?._id && !isHajjGarageTransferTrip">
            <div>{{ $t("to-city") }}</div>
            <div class="bold-text">
              {{
                isLangRtl
                  ? data?.service?.endCity?.arabicName
                  : data?.service?.endCity?.name
              }}
            </div>
          </div>
        </div>
        <div style="position: relative">
          <div class="route"></div>
          <div class="point start" v-if="data?.driver?.garage?.garageLocation?.name" v-b-tooltip.hover="{
            title:
              data?.driver?.garage?.garageLocation?.name +
              '\n' +
              data?.driver?.garage?.garageLocation?.location,
            placement: isLangRtl ? 'righttop' : 'lefttop',
          }"></div>
          <div class="point" :style="isLangRtl ? 'right: 38%;' : 'left: 38%;'" :class="{
            'pick-up': data?.driver?.garage?.garageLocation?.name,
            start: !data?.driver?.garage?.garageLocation?.name,
          }" v-if="data?.startLocation?.name" v-b-tooltip.hover="{
              title: data?.startLocation?.name,
              placement:
                isLangRtl && !data?.driver?.garage?.garageLocation?.name
                  ? 'righttop'
                  : 'lefttop',
            }"></div>
          <div class="point" :style="isLangRtl ? 'left: 38%;' : 'right: 38%;'" :class="{
            'drop-off': data?.driver?.garage?.garageLocation?.name,
            end: !data?.driver?.garage?.garageLocation?.name,
          }" v-if="displayEndLocationName" v-b-tooltip.hover="{
              title: displayEndLocationName,
              placement:
                isLangRtl && !data?.driver?.garage?.garageLocation?.name
                  ? 'lefttop'
                  : 'righttop',
            }"></div>
          <div class="point end" v-if="data?.driver?.garage?.garageLocation?.name" v-b-tooltip.hover.lefttop="data?.driver?.garage?.garageLocation?.name +
            '\n' +
            data?.driver?.garage?.garageLocation?.location
            "></div>
        </div>
        <div class="d-flex justify-content-between mt-3">
          <div :class="['place', `${isLangRtl ? 'end' : 'start'}`]" v-if="data?.driver?.garage?.garageLocation?.name">
            {{ $t("garage") }}
          </div>
          <div :class="[
            'place',
            `${isLangRtl ? 'end' : 'start'}`,
            { 'drop-off': data?.driver?.garage?.garageLocation?.name },
          ]" v-if="data?.startLocation?.name">
            {{ $t("pick-up-location") }}
          </div>
          <div :class="[
            'place',
            `${isLangRtl ? 'start' : 'end'}`,
            { 'drop-off': data?.driver?.garage?.garageLocation?.name },
          ]" v-if="displayEndLocationName">
            {{ isHajjGarageTransferTrip ? displayEndLocationName : $t("drop-off-location") }}
          </div>
          <div :class="['place', `${isLangRtl ? 'start' : 'end'}`]" v-if="data?.driver?.garage?.garageLocation?.name">
            {{ $t("garage") }}
          </div>
        </div>
      </div>
      <div class="d-flex align-items-center justify-content-betweem">
        <div style="flex: 0.8">
          <div class="mb-3" v-if="
            data?.actualStartTime ||
            data?.deviationInStartTime ||
            data?.actualEndTime ||
            data?.deviationInEndTime
          ">
            <div class="d-flex align-items-center mb-2">
              <div class="icon-container">
                <img src="@/assets/images/icons/clock.svg" alt="clock" />
              </div>
              <div style="margin-inline-start: 0.5rem">
                {{ $t("trip-timing-details") }}
              </div>
            </div>
            <div class="row">
              <div class="col-lg-3 col-md-6 col-xs-12" v-if="data?.actualStartTime">
                <div>{{ $t("actual-start-time") }}</div>
                <div class="bold-text">{{ getActualStartTime }}</div>
              </div>
              <div class="col-lg-3 col-md-6 col-xs-12" v-if="data?.deviationInStartTime">
                <div>{{ $t("deviation-in-start-time") }}</div>
                <div class="bold-text">{{ getDeviationInStartTime }}</div>
              </div>
              <div class="col-lg-3 col-md-6 col-xs-12" v-if="data?.actualEndTime">
                <div>{{ $t("actual-end-time") }}</div>
                <div class="bold-text">{{ getActualEndTime }}</div>
              </div>
              <div class="col-lg-3 col-md-6 col-xs-12" v-if="data?.deviationInEndTime">
                <div>{{ $t("deviation-in-end-time") }}</div>
                <div class="bold-text">{{ getDeviationInEndTime }}</div>
              </div>
            </div>
          </div>
          <div>
            <div class="d-flex align-items-center mb-2">
              <div class="icon-container">
                <img src="@/assets/images/icons/calendar-03.svg" alt="calendar-03" />
              </div>
              <div style="margin-inline-start: 0.5rem">
                {{ $t("dates-and-time") }}
              </div>
            </div>
            <div class="row">
              <div class="col-lg-3 col-md-6 col-xs-12">
                <div>{{ $t("start-date") }}</div>
                <div class="bold-text">{{ getStartDate }}</div>
              </div>
              <div class="col-lg-3 col-md-6 col-xs-12">
                <div>{{ $t("start-time") }}</div>
                <div class="bold-text">{{ getStartTime }}</div>
              </div>
              <div class="col-lg-3 col-md-6 col-xs-12">
                <div>{{ $t("end-date") }}</div>
                <div class="bold-text">{{ getEndDate }}</div>
              </div>
              <div class="col-lg-3 col-md-6 col-xs-12">
                <div>{{ $t("end-time") }}</div>
                <div class="bold-text">{{ getEndTime }}</div>
              </div>
            </div>
          </div>
        </div>
        <div style="flex: 0.2">
          <div class="mb-3 assign-block" v-if="data?.actualTripTime">
            <div>{{ $t("actual-trip-time") }}</div>
            <div class="bold-text">15:00 minutes</div>
          </div>
          <div class="assign-block" v-if="data?.totalDeviation">
            <div>{{ $t("total-deviation") }}</div>
            <div class="bold-text">00:50 minutes</div>
          </div>
        </div>
      </div>
      <hr />
      <div v-if="
        data?.initialOdoMeter ||
        data?.finalOdoMeter ||
        data?.distance ||
        data?.deviationInDistance
      ">
        <div class="d-flex align-items-center mb-3">
          <div style="flex: 0.8">
            <div class="d-flex align-items-center mb-2">
              <div class="icon-container">
                <img src="@/assets/images/icons/clock.svg" alt="clock" />
              </div>
              <div style="margin-inline-start: 0.5rem">
                {{ $t("trip-distance-details") }}
              </div>
            </div>
            <div class="row">
              <div class="col-lg-3 col-md-6 col-xs-12" v-if="data?.initialOdoMeter">
                <div>{{ $t("initial-odometer-value") }}</div>
                <div class="bold-text">{{ data?.initialOdoMeter.toFixed(2) }} KM</div>
              </div>
              <div class="col-lg-3 col-md-6 col-xs-12" v-if="data?.finalOdoMeter">
                <div>{{ $t("final-odometer-value") }}</div>
                <div class="bold-text">{{ data?.finalOdoMeter.toFixed(2) }} KM</div>
              </div>
              <div class="col-lg-3 col-md-6 col-xs-12" v-if="data?.distance">
                <div>{{ $t("actual-distance") }}</div>
                <div class="bold-text">{{ data?.distance.toFixed(2) }} KM</div>
              </div>
              <div class="col-lg-3 col-md-6 col-xs-12" v-if="data?.deviationInDistance">
                <div>{{ $t("deviation-in-distance") }}</div>
                <div class="bold-text">{{ data?.deviationInDistance.toFixed(2) }} KM</div>
              </div>
            </div>
          </div>
        </div>
        <hr />
      </div>
      <div class="row">
        <div class="col-lg-6 col-md-6 col-xs-12 mb-2" v-if="showMainDriverAssignment">
          <div class="assign-block d-flex justify-content-between align-items-center" :class="{
            'cursor-pointer': data?.mainDriver?._id,
          }" @click="moveTo('driver', data?.mainDriver?._id)">
            <div>
              <div class="d-flex align-items-center">
                <div class="avtar" v-if="data?.mainDriver?.vehiclePicture">
                  <img :src="data.mainDriver.vehiclePicture" alt="vehiclePicture" />
                </div>
                <div v-if="data?.mainDriver?._id">
                  <div class="bold-text title">
                    {{ $t("main-driver") }} :
                    {{
                      isLangRtl
                        ? data?.mainDriver?.arabicName
                        : data?.mainDriver?.name
                    }}
                  </div>
                  <div class="sub-title">
                    {{ $t("employee-id") }} :
                    <span class="bold-text">
                      #{{ data?.mainDriver?.employeeID }}
                    </span>
                  </div>
                </div>
                <div v-else>
                  <div class="bold-text title">{{ $t("main-driver") }}</div>
                  <div class="sub-title">{{ $t("driver-not-assigned") }}</div>
                </div>
              </div>
            </div>
            <div class="d-flex align-self-baseline gap-1">
              <b-button v-if="
                !$store.getters['enums/is_not_permitted'](
                  'assignDriversToJourney'
                ) &&
                data?.status !== 'Booking-Cancelled' &&
                data?.status !== 'Booking-Cancelled-Note' &&
                data?.status !== 'Trip-Arrived-To-Dropped-Off' &&
                data?.status !== 'Trip-Passenger-Dropped-Off' &&
                data?.status !== 'Trip-Completed' &&
                data?.status !== 'Trip-Completed-Note' &&
                (data?.status === 'Trip-Started'
                  ? !$store.getters['enums/is_not_permitted'](
                    'assignDriverForStartedTrip'
                  ) &&
                  !$store.getters['enums/is_not_permitted'](
                    'assignVehicleForStartedTrip'
                  )
                  : true)
              " class="reassign-btn" @click.stop="changeDriver(true)">
                {{
                  data?.mainDriver?._id ? $t("reassign") : $t("assign")
                }}
              </b-button>
              <b-button v-if="showReassignButton" class="reassign-btn" @click.stop="changeDriver(true)">
                {{
                  $t("reassign")
                }}
              </b-button>
              <b-button v-if="
                !$store.getters['enums/is_not_permitted'](
                  'assignDriversToJourney'
                ) &&
                !data?.status?.startsWith('Trip-') &&
                data?.mainDriver?._id
              " class="remove-btn" variant="danger" @click.stop="showRemoveDriverModal(true)">
                {{ $t("remove-driver") }}
              </b-button>
            </div>
          </div>
        </div>
        <div class="col-lg-6 col-md-6 col-xs-12 mb-2" v-if="data?.service?.numberOfDrivers > 1">
          <div class="assign-block d-flex justify-content-between align-items-center" :class="{
            'cursor-pointer': data?.secondaryDriver?._id,
          }" @click="moveTo('driver', data?.secondaryDriver?._id)">
            <div>
              <div class="d-flex align-items-center">
                <div class="avtar" v-if="data?.secondaryDriver?.vehiclePicture">
                  <img :src="data.secondaryDriver.vehiclePicture" alt="vehiclePicture" />
                </div>
                <div v-if="data?.secondaryDriver?._id">
                  <div class="bold-text title">
                    {{ $t("secondary-driver") }} :
                    {{
                      isLangRtl
                        ? data?.secondaryDriver?.arabicName
                        : data?.secondaryDriver?.name
                    }}
                  </div>
                  <div class="sub-title">
                    {{ $t("employee-id") }} :
                    <span class="bold-text">
                      #{{ data?.secondaryDriver?.employeeID }}
                    </span>
                  </div>
                </div>
                <div v-else>
                  <div class="bold-text title">
                    {{ $t("secondary-driver") }}
                  </div>
                  <div class="sub-title">
                    {{ $t("driver-not-assigned") }}
                  </div>
                </div>
              </div>
            </div>
            <div class="d-flex align-self-baseline gap-1">
              <b-button v-if="
                !$store.getters['enums/is_not_permitted'](
                  'assignDriversToJourney'
                ) &&
                data?.status !== 'Booking-Cancelled' &&
                data?.status !== 'Booking-Cancelled-Note' &&
                data?.status !== 'Trip-Arrived-To-Dropped-Off' &&
                data?.status !== 'Trip-Passenger-Dropped-Off' &&
                data?.status !== 'Trip-Completed' &&
                data?.status !== 'Trip-Completed-Note' &&
                (data?.status === 'Trip-Started'
                  ? !$store.getters['enums/is_not_permitted'](
                    'assignDriverForStartedTrip'
                  ) &&
                  !$store.getters['enums/is_not_permitted'](
                    'assignVehicleForStartedTrip'
                  )
                  : true)
              " class="reassign-btn" @click.stop="changeDriver(false)">
                {{
                  data?.secondaryDriver?._id ? $t("reassign") : $t("assign")
                }}
              </b-button>
              <b-button v-if="showReassignButton && data?.secondaryDriver?._id" class="reassign-btn"
                @click.stop="changeDriver(false)">
                {{ $t("reassign") }}
              </b-button>
              <b-button v-if="
                !$store.getters['enums/is_not_permitted'](
                  'assignDriversToJourney'
                ) &&
                !data?.status?.startsWith('Trip-') &&
                data?.secondaryDriver?._id
              " class="remove-btn" variant="danger" @click.stop="showRemoveDriverModal(false)">
                {{ $t("remove-driver") }}
              </b-button>
            </div>
          </div>
        </div>
        <div class="col-lg-6 col-md-6 col-xs-12 mb-2">
          <div class="assign-block d-flex justify-content-between align-items-center" :class="{
            'cursor-pointer': data?.vehicle?._id,
          }" @click="moveTo('vehicle', data?.vehicle?._id)">
            <div>
              <div class="d-flex align-items-center">
                <div class="avtar" v-if="data?.vehicle?.vehiclePicture">
                  <img :src="data.vehicle.vehiclePicture" alt="vehiclePicture" />
                </div>
                <div v-if="data?.vehicle?._id">
                  <div class="bold-text title">
                    {{ $t("plate") }} #{{ data?.vehicle?.plateNumber }}
                  </div>
                  <div class="sub-title">
                    {{ $t("operational-id") }} :
                    <span class="bold-text">
                      #
                      {{ data?.vehicle?.operationalID }}
                    </span>
                  </div>
                  <div class="sub-title">
                    {{ $t("brand") }} :
                    <span class="bold-text">
                      {{
                        isLangRtl
                          ? data?.vehicle?.make?.arabicName
                          : data?.vehicle?.make?.name
                      }}
                    </span>
                  </div>
                  <div class="sub-title">
                    {{ $t("model") }} :
                    <span class="bold-text">
                      {{
                        isLangRtl
                          ? data?.vehicle?.model?.arabicName
                          : data?.vehicle?.model?.name
                      }}
                    </span>
                  </div>
                  <div class="sub-title">
                    {{ $t("year-of-making") }} :
                    <span class="bold-text">
                      {{ data?.vehicle?.makeYear }}
                    </span>
                  </div>
                </div>
                <div v-else class="sub-title">
                  {{ $t("vehicle-not-assigned") }}
                </div>
              </div>
            </div>
            <div class="d-flex align-self-baseline gap-1">
              <b-button v-if="
                !$store.getters['enums/is_not_permitted'](
                  'assignVehiclesToJourney'
                ) &&
                data?.status !== 'Booking-Cancelled' &&
                data?.status !== 'Booking-Cancelled-Note' &&
                data?.status !== 'Trip-Arrived-To-Dropped-Off' &&
                data?.status !== 'Trip-Passenger-Dropped-Off' &&
                data?.status !== 'Trip-Completed' &&
                data?.status !== 'Trip-Completed-Note' &&
                (data?.status === 'Trip-Started'
                  ? !$store.getters['enums/is_not_permitted'](
                    'assignDriverForStartedTrip'
                  ) &&
                  !$store.getters['enums/is_not_permitted'](
                    'assignVehicleForStartedTrip'
                  )
                  : true)
              " class="reassign-btn" @click.stop="changeVehicles">
                {{
                  data?.vehicle?._id ? $t("reassign") : $t("assign")
                }}
              </b-button>
              <b-button v-if="
                !$store.getters['enums/is_not_permitted'](
                  'assignVehiclesToJourney'
                ) &&
                !data?.status?.startsWith('Trip-') &&
                data?.vehicle?._id
              " class="remove-btn" variant="danger" @click.stop="showRemoveVehicleModal">
                {{ $t("remove-vehicle") }}
              </b-button>
            </div>
          </div>
        </div>
      </div>
      <div class="row mt-2 mb-3">
        <div class="col-lg-6 col-md-6 col-xs-12">
          <div class="criteria-block d-flex justify-content-between align-items-center">
            <div class="d-flex align-items-center">
              <div>
                <div class="bold-text title">
                  {{ $t("driver-criteria") }}
                </div>
                <div class="sub-title" v-if="driverCriteria?.nationality">
                  {{ $t("nationality") }} :
                  <span class="bold-text">
                    {{ driverCriteria?.nationality }}
                  </span>
                </div>
                <div class="sub-title" v-if="driverCriteria?.spokenLanguages">
                  {{ $t("spoken-languages") }} :
                  <span class="bold-text">
                    {{ driverCriteria?.spokenLanguages }}
                  </span>
                </div>
                <div class="sub-title" v-if="driverCriteria?.religion">
                  {{ $t("religion") }} :
                  <span class="bold-text">
                    {{ driverCriteria?.religion }}
                  </span>
                </div>
                <div v-if="!driverCriteria?.nationality && !driverCriteria?.spokenLanguages && !driverCriteria.religion"
                  class="sub-title">
                  {{ $t("criteria-not-assign") }}
                </div>
              </div>
            </div>
          </div>
        </div>
        <div class="col-lg-6 col-md-6 col-xs-12">
          <div class="criteria-block d-flex justify-content-between align-items-center">
            <div class="d-flex align-items-center">
              <div>
                <div class="bold-text title">
                  {{ $t("vehicle-criteria") }}
                </div>
                <div class="sub-title" v-if="vehicleCriteria?.type">
                  {{ $t("vehicle-name") }} :
                  <span class="bold-text">
                    {{ vehicleCriteria?.type }}
                  </span>
                </div>
                <div class="sub-title" v-if="vehicleCriteria?.color">
                  {{ $t("color") }} :
                  <span class="bold-text">
                    {{ vehicleCriteria?.color }}
                  </span>
                </div>
                <div v-if="!vehicleCriteria?.type && !vehicleCriteria?.color" class="sub-title">
                  {{ $t("criteria-not-assign") }}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      <div class="row mt-2 mb-3">
        <hr />
        <div v-if="contactPersonDetails" class="col-xs-12 mb-2">
          <div class="bold-text" style="margin-bottom: 18px;">{{ $t("contact-person") }}</div>
          <div class="services-block-container">
            <div class="services-block">
              <div class="d-flex flex-column">
                <div>{{ $t("name") }}: <span class="bold-text">{{ isLangRtl ? contactPersonDetails?.arabicName :
                    contactPersonDetails?.name }}</span></div>
              </div>
            </div>
            <div class="services-block">
              <div class="d-flex flex-column">
                <div>{{ $t("mobile-number") }}: <span class="bold-text">{{ contactPersonDetails?.mobileNumber }}</span>
                </div>
              </div>
            </div>
            <div class="services-block">
              <div class="d-flex flex-column">
                <div>{{ $t("email-address") }}: <span class="bold-text">{{ contactPersonDetails?.email }}</span></div>
              </div>
            </div>
          </div>
        </div>
        <div v-else>
          <div class="bold-text">{{ $t("contact-empty") }}</div>
        </div>
      </div>
      <div class="row mt-2">
        <hr />
        <div v-if="tripService?.comment" class="col-lg-6 col-md-6 col-xs-12 mb-2">
          <div class="services-block">
            <div class="d-flex flex-column">
              <div class="bold-text">{{ $t("comment") }} : </div>
              <div>{{ tripService?.comment }}</div>
            </div>
          </div>
        </div>
        <div v-else>
          <div class="bold-text">{{ $t("comment-empty") }}</div>
        </div>
      </div>
      <div class="row mt-2" v-if="!$store.getters['enums/is_not_permitted']('transactionLogTab')">
        <hr />
        <div v-b-toggle="'collapse-logs-table'"
          class="product-container__header d-flex align-items-center justify-content-between flex-wrap">
          <div class="d-flex align-items-center flex-wrap">
            <h2 class="title">
              {{ $t("logs") }}
            </h2>
          </div>
          <img :class="[
            { 'rotate-180': logsShowing },
            { 'rotate-180-reverse': !logsShowing },
          ]" :src="require('@/assets/images/icons/arrow-down.svg')" alt="arrow" />
        </div>
        <b-collapse :id="'collapse-logs-table'" v-model="logsShowing">
          <div class="col-12 my-2" v-if="logsShowing">
            <TransactionTab :filterBy="{ id: data?._id, type: 'Trip' }" />
          </div>
        </b-collapse>
      </div>
      </div>
      <div v-if="data">
        <ChangeVehiclesModel :trip="data" @selectedVehicle="setSelectedVehicle"
          :selectedDriverId="mainDriverDetails?.id" />
        <ChangeDriverModel :trip="data" @selectedDriver="setSelectedDriver" :selectedVehicleId="vehicleDetails?._id"
          :isMainDriver="isMainDriver" :isReassign="showReassignButton" />
        <EditTrip :data="data" @refreshData="refreshData" />
        <CompleteWithNoteModal :id="data?._id" @updateTrip="updateTrip"
          :serviceAllowance="tripServiceAllowanceAmount" />
        <RemoveDriverModal :tripID="data?._id" @removeDriver="removeDriver" />
        <RemoveVehicleModal :tripID="data?._id" @removeVehicle="removeVehicle" />
        <CancelDeadHeadTripModal :tripID="data?._id" @cancelTrip="cancelTrip" />
        <HajjGarageTransferOrderPrintSheet
          ref="garageTransferPrintSheet"
          :payload="garageTransferPrintPayload"
        />
        <div
          v-if="garageTransferTripTablePrintBundle"
          class="hajj-garage-transfer-trip-table-offscreen"
          aria-hidden="true"
        >
          <HajjDawraOperationsFormPrintSheet
            ref="garageTransferTripTablePrintSheet"
            :bundle="garageTransferTripTablePrintBundle"
          />
        </div>
      </div>
      <div class="trip-detail-hide-on-transfer-print">
        <ActualRoute :data="data" :type="type" />
      </div>
      <div class="position-relative pdf-content trip-detail-hide-on-transfer-print">
        <OrderDetailsPDF :pdfContent="pdfContent" :servicesDetailsItems="servicesDetailsItems"
          :customerDetailsItems="customerDetailsItems" :pdfContentDisplay="pdfContentDisplay"
          :customerDetailsFields="customerDetailsFields" :servicesDetailsFields="servicesDetailsFields"
          :debitClientOrCredit="debitClientOrCredit" :showPriceField="false" />
        <VehicleInspectionReportPDF
          :pdfContentDisplay="inspectionPdfContentDisplay"
          :formData="inspectionFormData"
          :rightItems="inspectionRightItems"
          :leftItems="inspectionLeftItems"
          contentId="vehicleInspectionPdfContent"
        />
      </div>
    </div>
  </div>
</template>

<script>
import moment from "moment";
import TripService from "@/services/trip.service";
import VehicleService from "@/services/vehicle.service";
import ChangeVehiclesModel from "@/components/Operations/Details/JourneyContainerComponents/Vehicle/ChangeVehicle/ChangeVehiclesModel.vue";
import ChangeDriverModel from "@/components/Operations/Details/JourneyContainerComponents/Driver/ChangeDriver/ChangeDriversModel.vue";
import CompleteWithNoteModal from "./CompleteWithNoteModal.vue";
import ActualRoute from "./actualRoute.vue";
import ChangeTripStatus from "./changeTripStatus.vue";
import EditTrip from "./edit/index.vue";
import RemoveDriverModal from "./removeDriverModal.vue";
import RemoveVehicleModal from "./removeVehicleModal.vue";
import TransactionTab from "@/components/TransactionLog/TransactionTab";
import OrderDetailsPDF from "@/components/Operations/Details/OrderDetailsPDF.vue";
import VehicleInspectionReportPDF from "./VehicleInspectionReportPDF.vue";
import html2pdf from "html2pdf.js";
import CancelDeadHeadTripModal from "./cancelDeadHeadTripModal.vue";
import AllowancesSettingsService from "@/services/allowances-settings.service";
import VehicleOperationsService from "@/services/vehicleOperations.service";
import { buildInspectionChecklistFromApi } from "@/utilities/vehicleInspectionChecklist";
import HajjGarageTransferOrderPrintSheet from "@/components/HajjGarageTransfer/HajjGarageTransferOrderPrintSheet.vue";
import HajjDawraOperationsFormPrintSheet from "@/components/HajjDawraOperation/HajjDawraOperationsFormPrintSheet.vue";
import HajjGarageTransferService from "@/services/hajj-garage-transfer.service";
import {
  buildHajjGarageTransferPrintPayloadFromTrip,
  resolveHajjGarageTransferShowReassignNote,
} from "@/utils/hajj-garage-transfer-print";
import { buildHajjGarageTransferTripTablePrintBundleFromTrip } from "@/utils/hajj-garage-transfer-trip-table-print-bundle";

export default {
  props: ["id"],
  emits: ["update-flag", "change-tab"],
  components: {
    ChangeVehiclesModel,
    ChangeDriverModel,
    ActualRoute,
    CompleteWithNoteModal,
    ChangeTripStatus,
    EditTrip,
    RemoveDriverModal,
    RemoveVehicleModal,
    CancelDeadHeadTripModal,
    TransactionTab,
    OrderDetailsPDF,
    VehicleInspectionReportPDF,
    HajjGarageTransferOrderPrintSheet,
    HajjDawraOperationsFormPrintSheet,
  },
  data() {
    return {
      action_loading: false,
      loading: false,
      error: null,
      data: null,
      vehicleDetails: {},
      mainDriverDetails: {},
      secondaryDriverDetails: {},
      tripService: {},
      isMainDriver: false,
      type: "",
      garageTransferPrintLoading: false,
      garageTransferTripTablePrintLoading: false,
      garageTransferTripTablePrintBundle: null,
      showMapSelection: false,
      contactPersonDetails: null,
      driverCriteria: {
        spokenLanguages: null,
        nationality: null,
        religion: null,
      },
      vehicleCriteria: {
        type: null,
        color: null,
      },
      logsShowing: false,
      pdfLoading: false,
      inspectionChecklistExists: false,
      servicesDetailsFields: [
        {
          key: "recievingTime",
          label: "Recieving Time",
          class: "align-middle pb-3",
        },
        {
          key: "quantity",
          label: "Quantity",
          class: "align-middle pb-3",
          thStyle: { width: "30px" },
        },
        {
          key: "busType",
          label: "Bus Type",
          class: "align-middle pb-3",
          thStyle: { width: "80px" },
        },
        {
          key: "destination",
          label: "Destination",
          class: "align-middle pb-3",
          thStyle: { width: "160px" },
        },
        {
          key: "recievingArea",
          label: "Recieving Area",
          class: "align-middle pb-3",
          thStyle: { width: "160px" },
        },
        {
          key: "comment",
          label: "comment",
          class: "align-middle pb-3",
          thStyle: { width: "160px" },
        },
        {
          key: "endDate",
          label: "End Date",
          class: "align-middle pb-3",
          thStyle: { width: "50px" },
        },
        {
          key: "startDate",
          label: "Start Date",
          class: "align-middle pb-3",
          thStyle: { width: "50px" },
        },
        {
          key: "description",
          label: "Description",
          class: "align-middle pb-3",
          thStyle: { width: "160px" },
        },
        {
          key: "s",
          label: "S",
          class: "align-middle pb-3",
        },
      ],
      servicesDetailsItems: [],
      customerDetailsFields: [
        {
          key: "representativeContact",
          label: "Representative Contact",
          class: "align-middle pb-3",
        },
        {
          key: "representative",
          label: "Representative",
          class: "align-middle pb-3",
        },
        {
          key: "customerContact",
          label: "Customer Contact",
          class: "align-middle pb-3",
        },
        {
          key: "address",
          label: "Address",
          class: "align-middle pb-3",
        },
        {
          key: "customerName",
          label: "Customer Name",
          class: "align-middle pb-3",
        },
      ],
      customerDetailsItems: [],
      pdfContentDisplay: "none",
      inspectionPdfContentDisplay: "none",
      pdfContent: {},
      inspectionFormData: {},
      inspectionRightItems: [],
      inspectionLeftItems: [],
      debitClientOrCredit: "",
      tripServiceAllowanceAmount: null,
      allowanceTypeGroup: {},
      tripType: null,
    };
  },
  methods: {
    moveTo(page, id) {
      if (!id) return;
      const newWindow = window.open(`/${page}/${id}`, "_blank");
      newWindow?.focus();
    },
    showActualRouteModal(type) {
      this.type = type;
      this.$nextTick(() => {
        this.$bvModal.show("actual-route-modal");
      });
    },
    moveToEdit(trip) {
      this.$bvModal.show("modal-trip-" + trip);
    },
    showCancelTripModel() {
      this.$bvModal.show("cancel-deadhead-trip-model-" + this.data?._id);
    },
    changeDriver(isMainDriver) {
      this.isMainDriver = isMainDriver;
      this.$bvModal.show("assign-driver-model-" + this.data?._id);
    },
    changeVehicles() {
      this.$bvModal.show("assign-vehicle-model-" + this.data?._id);
    },
    setSelectedVehicle(selectedVehicleId) {
      this.loading = true;
      const data = {
        id: this.data._id,
        vehicle: selectedVehicleId,
      };
      TripService.assignVehicle("assign-vehicle", data)
        .then(() => {
          let params = {
            select:
              "make.name,make.arabicName,model.name,model.arabicName,category.name,category.arabicName,color,plateNumber,licenseNumber,vehiclePicture,makeYear,tgaNumber,chassisNumber,make.name,make.arabicName,operationalID",
          };
          VehicleService.get(selectedVehicleId, params)
            .then((response) => {
              this.data["vehicle"] = response;
              this.vehicleDetails = this.data?.vehicle;
            })
            .catch(({ message }) => {
              this.error = message;
            });

          this.getData(true);
        })
        .catch(({ message }) => {
          this.error = message;
        })
        .finally(() => {
          this.loading = false;
        });
    },
    setSelectedDriver(selectedDriver) {
      this.loading = true;

      const { _id } = this.data;
      const id = _id;
      const driverId = selectedDriver.id;

      let data = { id };
      let role, action;

      if (this.isMainDriver) {
        if (this.showReassignButton) {
          data.mainDriver = driverId;
          role = "main";
          action = "assign-driver-for-completed-trip";
        } else {
          data.driver = driverId;
          role = "main";
          action = "assign-driver";
        }
      } else {
        if (this.showReassignButton) {
          data.secondaryDriver = driverId;
          role = "secondary";
          action = "assign-driver-for-completed-trip";
        } else {
          data.driver = driverId;
          role = "secondary";
          action = "assign-secondary-driver";
        }
      }

      this.assignDriver(data, selectedDriver, role, action);
    },
    getContractId() {
      return this.data?.contract?._id || this.data?.contract?.id || null;
    },
    async isApprovedContractVehicle(vehicle) {
      const selectedVehicleId = vehicle?._id || vehicle?.id || vehicle;
      if (!selectedVehicleId) return false;

      const contractId = this.getContractId();
      if (!contractId) return true;

      try {
        const params = new URLSearchParams();
        params.append("contractName", contractId);
        params.append("_id", selectedVehicleId);
        params.append("limit", 1);
        params.append("select", "_id");

        const response = await VehicleService.search(params);
        return Boolean(response?.items?.length);
      } catch (_) {
        return false;
      }
    },
    assignDriver(data, selectedDriver, type, endpoint) {
      TripService.put(data, endpoint)
        .then(async () => {
          selectedDriver._id = selectedDriver.id;
          if (type === "main") {
            this.data.mainDriver = selectedDriver;
            this.data.driver = selectedDriver;
            this.mainDriverDetails = this.data.mainDriver;
            const shouldAutoAssignVehicle =
              selectedDriver.vehicle &&
              (await this.isApprovedContractVehicle(selectedDriver.vehicle));

            if (shouldAutoAssignVehicle) {
              this.setSelectedVehicle(selectedDriver.vehicle?._id || selectedDriver.vehicle);
            }
          } else {
            this.data.secondaryDriver = selectedDriver;
            this.secondaryDriverDetails = this.data.secondaryDriver;
          }
          this.$store.dispatch("enums/SHOW_TOASTER_POP", {
            message: this.showReassignButton ? this.$t("reassign-driver-success") : this.$t("assign-driver-success"),
            variant: "success",
            title: this.$t("success"),
            duration: 10000,
            noCloseButton: false,
            noAutoHide: false,
          });
          this.getData(true);
        })
        .catch(({ message }) => {
          this.error = message;
        })
        .finally(() => {
          this.loading = false;
        });
    },
    updateTrip() {
      this.getData();
      this.$emit("refetchData");
    },
    getData(changeTab) {
      this.getAllowanceSettings();
      if (this.id) {
        if (changeTab) this.action_loading = true;
        else this.loading = true;
        this.error = null;
        this.inspectionChecklistExists = false;
        let params = {
          select: "_id,reservation.client._id,reservation.client.arabicName,reservation.client.name,reservation.category._id,reservation.category.arabicName,reservation.category.name,service._id,service.arabicName,service.name,service.category._id,service.category.arabicName,service.category.name,status,subStatus,reservation._id,service.startCity._id,service.startCity._id,service.startCity.arabicName,service.startCity.name,service.endCity.arabicName,service.endCity.name,service.numberOfDrivers,driver.garage.garageLocation.name,driver.garage.garageLocation.location,startLocation.location,startLocation.name,endLocation.location,endLocation.name,actualStartTime,deviationInStartTime,actualEndTime,deviationInEndTime,actualTripTime,totalDeviation,initialOdoMeter,finalOdoMeter,distance,deviationInDistance,mainDriver.vehiclePicture,mainDriver._id,mainDriver.arabicName,mainDriver.name,mainDriver.employeeID,mainDriver.mobileNumber,driver._id,driver.arabicName,driver.name,driver.employeeID,driver.mobileNumber,secondaryDriver,secondaryDriver.vehiclePicture,secondaryDriver.arabicName,secondaryDriver.name,secondaryDriver.employeeID,secondaryDriver._id,vehicle.vehiclePicture,vehicle._id,vehicle.driver._id,vehicle.plateNumber,vehicle.operationalID,vehicle.make.arabicName,vehicle.make.name,vehicle.model.arabicName,vehicle.model.name,vehicle.makeYear,scheduledStartDate,scheduledStartTime,scheduledEndDate,scheduledEndTime,branch._id,reservation.client.contactPerson,reservation.id,reservation.branch.arabicName,reservation.branch.name,reservation.status,reservation.type,reservation.numberOfVehicles,reservation.quantity,reservationService,service.allowanceAmount,driverCriteria,vehicleCriteria,isDeadhead,uniqueServiceId,permenantDeadhead,deadheadDestinationGarage._id,deadheadDestinationGarage.name,deadheadDestinationGarage.arabicName,hajjGarageTransferReassignment,hajjGarageTransferDriverPrintName,hajjGarageTransferPrint,contract._id,contract.id,contract.contactPerson,contract.client.name,contract.client.arabicName,contract.client.contactPerson,contractService,contract.branch.name,contract.branch.arabicName,contract.services,route._id,route.name,route.arabicName,route.assignedVehicles,isDeadheadAllowance",
        };

        TripService.get(this.id, params)
          .then((res) => {
            this.tripServiceAllowanceAmount = this.getServiceAllowance(res);
            this.data = res;
            this.data.contract ? this.tripType = "CONTRACT" : this.tripType = "CHARTER";
            this.vehicleDetails = this.data?.vehicle;
            this.mainDriverDetails = this.data?.mainDriver;
            this.secondaryDriverDetails = this.data?.secondaryDriver;
            this.tripService = this.data?.reservation
              ? this.data.reservationService || {}
              : this.data?.contractService || {};
            this.contactPersonDetails = this.data?.[this.data?.reservation ? "reservation" : "contract"]?.client?.contactPerson?.find((each) => each?._id === this?.tripService?.contactPerson) || null;
            // Vehicle Criteria
            this.vehicleCriteria.type = this.getVehicleTypeText(
              this.data?.isDeadhead && !this.data?.tripService ? this.data?.vehicleCriteria?.vehicleType : this.tripService?.vehicleCriteria?.vehicleType);
            this.vehicleCriteria.color = this.data?.isDeadhead && !this.data?.tripService ? this.data?.vehicleCriteria?.color : this.tripService?.vehicleCriteria?.color;

            // Driver Criteria
            this.driverCriteria.nationality =
              this.getNationalitiesText(this.tripService?.driverCriteria?.nationality) ??
              (this.data?.isDeadhead && !this.data?.tripService ? this.getNationalitiesText(this.data?.driverCriteria?.nationality) : null);

            this.driverCriteria.spokenLanguages = this.getLanguagesText(this.tripService?.driverCriteria?.languages) ??
              (this.data?.isDeadhead && !this.data?.tripService ? this.getLanguagesText(this.data?.driverCriteria?.languages) : null);

            this.driverCriteria.religion = this.getReligionText(
              this.data?.isDeadhead && !this.data?.tripService && this.data?.driverCriteria?.religion ? this.data?.driverCriteria?.religion : this.tripService?.driverCriteria?.religion
            );
            if (changeTab) this.$emit("change-tab", res?.subStatus);

            return VehicleOperationsService.checklistExists(this.id)
              .then((existsResponse) => {
                this.inspectionChecklistExists = Boolean(
                  existsResponse?.exists ?? existsResponse?.data?.exists
                );
              })
              .catch(() => {
                this.inspectionChecklistExists = false;
              });
          })
          .catch(({ message }) => {
            this.error = message;
            this.inspectionChecklistExists = false;
          })
          .finally(() => {
            if (changeTab) this.action_loading = false;
            else this.loading = false;
          });
      }
    },
    getServiceAllowance(tripServiceRes) {
      let allowanceAmount = 0;
      if(tripServiceRes.contract){
        const service = tripServiceRes?.contract?.services?.find(s => s.uniqueServiceId === tripServiceRes?.contractService?.uniqueServiceId);
        allowanceAmount = service?.allowanceSetup?.allowanceTypeAmount || 0;
      }else{
        allowanceAmount = tripServiceRes?.service?.allowanceAmount || 0;
      }
      return allowanceAmount;
    },
    async getAllowanceSettings() {
      try {
        const res = await AllowancesSettingsService.get("");
        const items = Array.isArray(res) ? res : res.items || [];

        this.allowanceTypeGroup = items.reduce((prev, curr) => {
          const type = curr.type;
          if (prev[type]) {
            prev[type].push(curr);

          } else {
            prev[type] = [curr];
          }
          return prev;
        }, {});
      } catch (err) {
        this.error = err?.message || "Error loading allowances";
      } finally {
        this.content_loading = false;
      }
    },
    viewed() {
      this.action_loading = true;
      this.error = null;
      TripService.patch(`${this.id}/viewed`)
        .then(() => {
          this.getData(true);
        })
        .catch(({ message }) => {
          this.error = message;
          this.action_loading = false;
        });
    },
    unview() {
      this.action_loading = true;
      this.error = null;

      TripService.patch(`${this.id}/unview`)
        .then(() => {
          this.getData(true);
        })
        .catch(({ message }) => {
          this.error = message;
          this.action_loading = false;
        });
    },
    refreshData(changeTab) {
      this.getData(changeTab);
      this.$emit("refetchData");
    },
    toggleShowMap() {
      this.showMapSelection =
        !this.showMapSelection;
    },
    hajjGarageTransferRefId(ref) {
      if (ref == null || ref === "") return "";
      if (typeof ref === "object" && ref._id != null) return String(ref._id);
      return String(ref);
    },
    resolveHajjGarageTransferAssignedDriver(trip) {
      const md = trip?.mainDriver;
      const dr = trip?.driver;
      if (md && typeof md === "object" && (md._id || md.name || md.arabicName)) return md;
      if (dr && typeof dr === "object" && (dr._id || dr.name || dr.arabicName)) return dr;
      return {};
    },
    hajjGarageTransferShowReassignNote(trip, snap) {
      return resolveHajjGarageTransferShowReassignNote(trip, snap);
    },
    resolveGarageTransferTripTablePrintContext() {
      const snap = this.data?.hajjGarageTransferPrint || {};
      let vehicleTypeText = this.getVehicleTypeText(this.data?.vehicleCriteria?.vehicleType);
      if (vehicleTypeText && this.$te(String(vehicleTypeText))) {
        vehicleTypeText = this.$t(String(vehicleTypeText));
      }
      return {
        snap,
        vehicleTypeText: vehicleTypeText || snap.vehicleTypeText || "",
        destinationText: this.displayEndLocationName || snap.destinationText || "—",
        serviceText: snap.serviceText || this.servicePrintNameDetail,
        createdByPrintName: snap.createdByPrintName || "",
      };
    },
    async printGarageTransferOrder() {
      if (!this.id) return;
      this.garageTransferPrintLoading = true;
      this.error = null;
      try {
        await this.getData(true);
        const { snap, vehicleTypeText, destinationText, serviceText } =
          this.resolveGarageTransferTripTablePrintContext();
        const localPayload = buildHajjGarageTransferPrintPayloadFromTrip(this.data, {
          isLangRtl: this.isLangRtl,
          destinationText,
          serviceText,
          vehicleTypeText,
        });
        const apiPayload = await HajjGarageTransferService.getPrintPayload(this.id);
        const payload = {
          ...localPayload,
          ...apiPayload,
          showReassignNote: Boolean(apiPayload?.showReassignNote || localPayload?.showReassignNote),
        };
        await this.$nextTick();
        await this.$refs.garageTransferPrintSheet?.runPrint(payload);
      } catch (e) {
        const msg = e?.response?.data?.message || e?.message;
        this.error = Array.isArray(msg) ? msg : msg || this.$t("something-went-wrong");
      } finally {
        this.garageTransferPrintLoading = false;
      }
    },
    async printGarageTransferTripTable() {
      if (!this.id) return;
      this.garageTransferTripTablePrintLoading = true;
      this.error = null;
      try {
        await this.getData(true);
        const { vehicleTypeText, destinationText, serviceText, createdByPrintName } =
          this.resolveGarageTransferTripTablePrintContext();
        const bundle = buildHajjGarageTransferTripTablePrintBundleFromTrip(this.data, {
          isLangRtl: this.isLangRtl,
          serviceSubTypeLabel: this.$t("hajj-garage-transfer-trip-table-subtype"),
          destinationText,
          vehicleTypeText,
          serviceText,
          createdByPrintName,
        });
        if (!bundle?.tripPages?.length) {
          throw new Error(this.$t("something-went-wrong"));
        }
        this.garageTransferTripTablePrintBundle = bundle;
        await this.$nextTick();
        await this.$refs.garageTransferTripTablePrintSheet?.downloadPdf(
          `Hajj-Transfer-${bundle.operationId || this.id}.pdf`,
        );
      } catch (e) {
        const msg = e?.response?.data?.message || e?.message;
        this.error = Array.isArray(msg) ? msg : msg || this.$t("something-went-wrong");
      } finally {
        this.garageTransferTripTablePrintBundle = null;
        this.garageTransferTripTablePrintLoading = false;
      }
    },
    showRemoveDriverModal(isMainDriver) {
      this.isMainDriver = isMainDriver;
      this.$bvModal.show("remove-driver-model-" + this.data?._id);
    },
    showRemoveVehicleModal() {
      this.$bvModal.show("remove-vehicle-model-" + this.data?._id);
    },
    removeDriver() {
      let tripServiceAction = this.isMainDriver ? "unassignDriver" : "unassignSecondaryDriver";
      this.loading = true;
      const data = {
        id: this.data._id,
      };
      TripService[tripServiceAction](data)
        .then(() => {
          this.getData(true);
        })
        .catch(({ message }) => {
          this.error = message;
        })
        .finally(() => {
          this.loading = false;
        });
    },
    removeVehicle() {
      this.loading = true;
      const data = {
        id: this.data._id,
      };
      TripService.put(data, "unassign-vehicle")
        .then(() => {
          this.getData(true);
        })
        .catch(({ message }) => {
          this.error = message;
        })
        .finally(() => {
          this.loading = false;
        });
    },
    async generatePDF(createFile) {
      return new Promise((resolve, reject) => {
        this.pdfContentDisplay = "none";
        if (!createFile) this.pdfLoading = true;
        this.error = null;

        let params = {
          select:
            "_id,type,branch.name,branch.arabicName,client.contactPerson.name,client.contactPerson.mobileNumber,client.vatNumber,servicesTotalPrice,finalPriceIncludingTax,client.phoneNumber,client.address,client.name,client.arabicName,client.created_on,services.service.price,services.service.pricingQuantity,services.service.taxType.percentage,services.service.toTime,services.service.serviceQuantity,services.service.vehicleCriteria,services.service.dropoffLocation,services.service.pickupLocation,services.service.toDate,services.service.fromDate,services.service.isProduct,discountPercentage,discountAmount,finalPrice,taxAmount,created_by,referenceNumber,contactPerson",
        };

        TripService.get(`trip-reservation/${this.id}`, params)
          .then((res) => {
            this.pdfContent = {
              id: res?._id,
              type: res?.type === "Charter" ? "Transportation Contract" : res?.type,
              branchEnglishName: res?.branch?.name,
              branchArabicName: res?.branch?.arabicName,
              taxNo: res?.client?.vatNumber,
              servicesTotalPrice: res?.servicesTotalPrice,
              finalPriceIncludingTax: res?.finalPriceIncludingTax?.toFixed(2),
              discountPercentage: res?.discountPercentage || "---",
              discountAmount: res?.discountAmount || "---",
              discountAfter: res?.finalPrice || "---",
              valueVAT: res?.taxAmount || "---",
              vatRateValue: res?.services.some((service) => {
                return service?.service?.taxType?.find(
                  (tax) => tax.percentage === 15
                );
              })
                ? "15"
                : "0",
              clientName: res?.created_by,
              confirmNo: res?.referenceNumber || "---",
            };
            this.customerDetailsItems = [
              {
                representativeContact:
                  res?.client?.contactPerson.find(el => el._id === res.contactPerson)?.mobileNumber || "---",
                representative: res?.client?.contactPerson.find(el => el._id === res.contactPerson)?.name || "---",
                customerContact: res?.client?.phoneNumber,
                address: res?.client?.address,
                customerName: this.isLangRtl ? res?.client?.arabicName : res?.client?.name,
                startDate: moment(res?.client?.created_on)
                  .locale("en")
                  .utcOffset(0)
                  .format("DD-MM-YYYY"),
                endDate: "---",
              },
            ];
            this.debitClientOrCredit = res?.client?.paymentMethod === 'Debit' ? "Cash / نقداً" : "Postpaid / آجل";

            this.servicesDetailsItems = res?.services?.map((service, index) => {
              return {
                amount: service?.finalTotalPriceWithTax + "SR",
                vat:
                  service?.service?.taxType?.reduce(
                    (accumulator, currentValue) =>
                      accumulator + currentValue?.percentage,
                    0
                  ) + "%",
                recievingTime: service?.fromTime,
                quantity: service?.serviceQuantity,
                busType:
                  (this.$store.getters["enums/brand_enums"]?.find(
                    (brand) => brand.value === service?.vehicleCriteria?.make
                  )?.text || "---") +
                  " - " +
                  (this.$store.getters["enums/model_enums"]?.find(
                    (model) => model.value === service?.vehicleCriteria?.model
                  )?.text || "---"),
                destination: this.shortText(service?.dropoffLocation?.name) || "---",
                recievingArea: this.shortText(service?.pickupLocation?.name) || "---",
                endDate: moment(service?.toDate).locale("en").format("YYYY-MM-DD"),
                startDate: moment(service?.fromDate).locale("en").format("YYYY-MM-DD"),
                description: this.shortText(service?.description) || "---",
                comment: this.shortText(service?.comment) || "---",
                s: index + 1,
              };
            });

            this.pdfContentDisplay = "block";
            const element = document.getElementById("pdfContent");
            const pdfOptions = {
              margin: [0.2, 0.2, 0.2, 0.2],
              filename: "clientStatement.pdf",
              image: { type: "jpeg", quality: 0.98 },
              html2canvas: { scale: 2, useCORS: true },
              jsPDF: { unit: "in", format: "letter", orientation: "portrait" },
            };

            let pdfUrl;
            const reservationID = this.data?.reservation?._id;
            html2pdf()
              .set(pdfOptions)
              .from(element)
              .toPdf()
              .get("pdf")
              .then(function (pdf) {
                pdf.setProperties({
                  title: `Reservation Details - ${reservationID}`,
                  subject: `Reservation Details - ${reservationID}`,
                  author: `Reservation Details - ${reservationID}`,
                  keywords: "pdf, document",
                });
                pdfUrl = pdf.output(createFile ? "blob" : "bloburl");
                return pdfUrl;
              })
              .then((pdfUrl) => {
                if (createFile) {
                  resolve(
                    new File([pdfUrl], `Sales Order-${reservationID}.pdf`, {
                      type: pdfUrl.type,
                    })
                  );
                } else {
                  this.pdfUrl = pdfUrl;
                  this.pdfContentDisplay = "none";

                  const url = pdfUrl;
                  const link = document.createElement("a");
                  link.href = url;
                  link.setAttribute("download", `Sales Order-${reservationID}`);
                  link.style.display = "none";
                  document.body.appendChild(link);
                  link.click();
                  document.body.removeChild(link);
                }
              });
          })
          .catch((error) => {
            this.error = error?.message;
            reject(error);
          })
          .finally(() => {
            if (!createFile) this.pdfLoading = false;
          });
      });
    },
    buildInspectionFormData() {
      const route = [this.data?.startLocation?.name, this.displayEndLocationName]
        .filter(Boolean)
        .join(" - ");

      return {
        date: moment().locale("en").format("DD-MM-YYYY"),
        tripNo: this.data?._id || "---",
        station: route || "---",
        route: route || "---",
        driver1Name: this.mainDriverDetails?.name || "---",
        driver1Id: this.mainDriverDetails?.employeeID || "---",
        driver2Name: this.secondaryDriverDetails?.name || "---",
        vehicleNo:
          this.vehicleDetails?.plateNumber || this.vehicleDetails?.operationalID || "---",
        kmOut: this.data?.initialOdoMeter || "---",
        dateTimeIn: this.data?.actualEndTime
          ? moment(this.data.actualEndTime).locale("en").format("DD-MM-YYYY HH:mm")
          : "---",
        kmIn: this.data?.finalOdoMeter || "---",
        checkedBy: "---",
        checkedByEmployeeId: "---",
        checkedBySignature: "---",
        notes: "---",
        firstDriverName: this.mainDriverDetails?.name || "---",
        firstDriverSignature: "---",
        firstDriverSecondEntry: "---",
        firstDriverSecondExit: "---",
        secondDriverName: this.secondaryDriverDetails?.name || "---",
        secondDriverSignature: "---",
        secondDriverSecondEntry: "---",
        secondDriverSecondExit: "---",
      };
    },
    buildInspectionPdfPayload(checklistResponse) {
      const { rightItems, leftItems } = buildInspectionChecklistFromApi(
        checklistResponse?.checklist || checklistResponse?.data?.checklist || []
      );
      const submittedBy = checklistResponse?.submittedBy || checklistResponse?.data?.submittedBy;
      const formData = this.buildInspectionFormData();
      if (submittedBy) {
        formData.checkedBy = submittedBy.name || submittedBy.arabicName || "---";
        formData.checkedByEmployeeId = submittedBy.employeeID || "---";
      }
      return {
        formData,
        rightItems,
        leftItems,
      };
    },
    async generateInspectionPDF(createFile) {
      try {
        this.inspectionPdfContentDisplay = "none";
        if (!createFile) this.pdfLoading = true;
        this.error = null;

        const tripID = this.data?._id;
        const checklistResponse = tripID
          ? await VehicleOperationsService.getChecklist(tripID)
          : null;
        const inspectionPdfPayload = this.buildInspectionPdfPayload(checklistResponse);

        this.inspectionFormData = inspectionPdfPayload.formData;
        this.inspectionRightItems = inspectionPdfPayload.rightItems;
        this.inspectionLeftItems = inspectionPdfPayload.leftItems;
        this.inspectionPdfContentDisplay = "block";

        await new Promise((resolve) => this.$nextTick(resolve));

        const element = document.getElementById("vehicleInspectionPdfContent");
        if (!element) {
          throw new Error("Inspection PDF content not found");
        }

        const pdfOptions = {
          margin: [0.2, 0.2, 0.2, 0.2],
          filename: "vehicle-inspection-report.pdf",
          image: { type: "jpeg", quality: 0.98 },
          html2canvas: { scale: 2, useCORS: true, letterRendering: true },
          pagebreak: { mode: ["avoid-all"] },
          jsPDF: { unit: "mm", format: "letter", orientation: "portrait" },
        };

        const generatedPdfUrl = await html2pdf()
          .set(pdfOptions)
          .from(element)
          .toPdf()
          .get("pdf")
          .then((pdf) => {
            while (pdf.internal.getNumberOfPages() > 1) {
              pdf.deletePage(pdf.internal.getNumberOfPages());
            }
            pdf.setProperties({
              title: `Vehicle Inspection Report - ${tripID}`,
              subject: `Vehicle Inspection Report - ${tripID}`,
              author: `Vehicle Inspection Report - ${tripID}`,
              keywords: "pdf, inspection, vehicle",
            });

            return pdf.output(createFile ? "blob" : "bloburl");
          });

        if (createFile) {
          return new File(
            [generatedPdfUrl],
            `Vehicle-Inspection-Report-${tripID}.pdf`,
            { type: generatedPdfUrl.type }
          );
        }

        const link = document.createElement("a");
        link.href = generatedPdfUrl;
        link.setAttribute("download", `Vehicle-Inspection-Report-${tripID}`);
        link.style.display = "none";
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      } catch (error) {
        this.error = error?.message;
        throw error;
      } finally {
        this.inspectionPdfContentDisplay = "none";
        if (!createFile) this.pdfLoading = false;
      }
    },
    cancelTrip() {
      this.loading = true;
      const data = {
        trips: [
          {
            id: this.data?._id,
          },
        ]
      };
      TripService.put(data, "v2/cancel")
        .then(() => {
          this.getData(true);
        })
        .catch(({ message }) => {
          this.error = message;
        })
        .finally(() => {
          this.loading = false;
        });
    },
    shortText(description) {
      if (!description) return;
      const words = description?.split(' ');
      if (words?.length > 12) {
        return words?.slice(0, 12).join(' ') + '...';
      }
      return description;
    },
    getNationalitiesText(nationalities) {
      return nationalities?.length && this.$store.getters["enums/nationality_enums"].length
        ? nationalities.map((each) => {
          const rec = this.$store.getters["enums/nationality_enums"]?.find((e) => e.code === each);
          return rec?.text;
        }).join(", ")
        : null;
    },
    getLanguagesText(languages) {
      return languages?.length && this.$store.getters["enums/language_enums"].length
        ? languages.map((each) => {
          const rec = this.$store.getters["enums/language_enums"]?.find((e) => e.code === each);
          return rec?.text;
        }).join(", ")
        : null;
    },
    getVehicleTypeText(value) {
      const vehicleTypeList = this.$store.getters["enums/vehicle_type_enums"] || [];
      const match = vehicleTypeList.find(e => e.value === value);

      if (!match || match.text === "select-an-option") {
        return null;
      }

      return match.text;
    },
    getReligionText(religion) {
      const religionList = this.$store.getters["enums/religion_enums"] || [];
      const match = religionList.find(e => e.value === religion);

      if (!match || match.text === "select-an-option") {
        return null;
      }

      return match.text;
    }
  },
  computed: {
    isTaseedOperationsTrip() {
      const uid = this.data?.uniqueServiceId;
      return typeof uid === "string" && uid.startsWith("TASEED-OPERATIONS-");
    },
    showMainDriverAssignment() {
      const drivers = Number(this.data?.service?.numberOfDrivers || 0);
      if (drivers > 0 || !this.data?.reservation) return true;
      return this.isTaseedOperationsTrip;
    },
    checkTime() {
      let a = moment(),
        b = moment();
      if (this.data?.scheduledStartDate && this.data?.scheduledStartTime) {
        const from_time_parts = this.data.scheduledStartTime.split(":");

        a = moment(this.data.scheduledStartDate)
          .set("hour", from_time_parts[0])
          .set("minute", from_time_parts[1])
          .set("seconds", 0);
        const minutes = a.diff(b, "minutes");
        // more than 24 hours
        return minutes > 1440 ? true : false;
      } else return true;
    },
    getStartDate() {
      return this.data?.scheduledStartDate
        ? moment(this.data.scheduledStartDate)
          .locale("en")
          .utcOffset(0)
          .format("DD-MM-YYYY")
        : "";
    },
    getStartTime() {
      return this.data?.scheduledStartTime
        ? moment(this.data.scheduledStartTime)
          .locale("en")
          .utcOffset(0)
          .format("HH:mm")
        : "";
    },
    getEndDate() {
      return this.data?.scheduledEndDate
        ? moment(this.data.scheduledEndDate)
          .locale("en")
          .utcOffset(0)
          .format("DD-MM-YYYY")
        : "";
    },
    getEndTime() {
      return this.data?.scheduledEndTime
        ? moment(this.data.scheduledEndTime)
          .locale("en")
          .utcOffset(0)
          .format("HH:mm")
        : "";
    },
    getActualStartTime() {
      return this.data?.actualStartTime
        ? moment(this.data.actualStartTime)
          .locale("en")
          .utcOffset(0)
          .format("HH:mm")
        : "";
    },
    getActualEndTime() {
      return this.data?.actualEndTime
        ? moment(this.data.actualEndTime)
          .locale("en")
          .utcOffset(0)
          .format("HH:mm")
        : "";
    },
    getDeviationInStartTime() {
      return this.data?.deviationInStartTime
        ? moment(this.data.deviationInStartTime)
          .locale("en")
          .utcOffset(0)
          .format("HH:mm")
        : "";
    },
    getDeviationInEndTime() {
      return this.data?.deviationInEndTime
        ? moment(this.data.deviationInEndTime)
          .locale("en")
          .utcOffset(0)
          .format("HH:mm")
        : "";
    },
    showCompleteWithNote() {
      return (
        this.data?._id &&
        !this.$store.getters["enums/is_not_permitted"]("changeJourneyStatus") &&
        (this.data.status === "Trip-Started" ||
          this.data.status === "Trip-Arrived-At-Pickup-Location" ||
          this.data.status === "Trip-Passenger-Picked-Up" ||
          this.data.status === "Trip-Passenger-Dropped-Off")
      );
    },
    allowEdit() {
      if (this.data?.isDeadhead)
        return false;

      return (
        !this.$store.getters['enums/is_not_permitted']('editTripByOperator') &&
        (!this.$store.getters['enums/is_not_permitted']('editTripLocationByOperator') || !this.$store.getters['enums/is_not_permitted']('editTripDateAndTimeByOperator')) &&
        this.data?.reservation?.status !== "Booking-Cancelled" &&
        this.data?.reservation?.status !== "Booking-Cancelled-Note" &&
        this.data?.status !== "Booking-Cancelled" &&
        this.data?.status !== "Booking-Cancelled-Note" &&
        this.data?.status !== "Trip-Arrived-To-Dropped-Off" &&
        this.data?.status !== "Trip-Passenger-Dropped-Off" &&
        this.data?.status !== "Trip-Completed" &&
        this.data?.status !== "Trip-Completed-Note" &&
        this.data?.status !== "Trip-Started"
      );
    },
    hasPermissionPrintSalesOrder() {
      return !this.$store.getters['enums/is_not_permitted']('printSalesOrderForOperation');
    },
    showInspectionPrintButton() {
      return (
        this.hasPermissionPrintSalesOrder &&
        this.inspectionChecklistExists
      );
    },
    showCancelButton() {
      const allowedStatuses = new Set([
        "Booking-Confirmed",
        "Booking-Re-Assigned",
        "Booking-Un-Assigned",
        "Booking-Pending"
      ]);

      const isAllowedToChangeStatus = !this.$store.getters["enums/is_not_permitted"]("editTripByOperator");
      const hasReservation = !!this.data?.reservation?._id;
      const tripStatus = this.data?.status;

      if (hasReservation || !isAllowedToChangeStatus) {
        return false;
      }

      return allowedStatuses.has(tripStatus);
    },
    isReservationTrip() {
      return !!this.data?.reservation;
    },
    clientName() {
      const reservationClient = this.data?.reservation?.client;
      const contractClient = this.data?.contract?.client;

      if (this.isReservationTrip) {
        return this.isLangRtl ? reservationClient?.arabicName : reservationClient?.name;
      } else {
        return this.isLangRtl ? contractClient?.arabicName : contractClient?.name;
      }
    },
    showReassignButton() {
      return (
        !this.$store.getters['enums/is_not_permitted'](
          'reAssignDriverForCompletedTrip'
        ) &&
        (this.data?.status === 'Trip-Completed' ||
          this.data?.status === 'Trip-Completed-Note'))
    },
    showMapButtonVisible() {
      if (!this.data?._id) return false;
      const statuses = [
        'Trip-Completed',
        'Trip-Completed-Note',
        'Trip-Arrived-To-Dropped-Off',
        'Trip-Passenger-Dropped-Off',
        'Trip-Passenger-Picked-Up',
        'Trip-Arrived-At-Pickup-Location',
        'Trip-Started',
      ];
      return statuses.includes(this.data.status);
    },
    isHajjGarageTransferTrip() {
      if (!this.data?.isDeadhead) return false;
      const id = this.data?.uniqueServiceId;
      if (!id) return false;
      const s = String(id);
      return s.startsWith("HAJJ-GARAGE-TRANSFER") || s.includes("HAJJ-GARAGE-TRANSFER");
    },
    displayEndLocationName() {
      if (!this.isHajjGarageTransferTrip) {
        return this.data?.endLocation?.name || "";
      }
      const snap = this.data?.hajjGarageTransferPrint || this.data?.hajjGarageTransferPrintPayload || {};
      const fromSnap = String(snap.destinationText || "").trim();
      if (fromSnap) return fromSnap;
      const g = this.data?.deadheadDestinationGarage;
      if (g) {
        const garageName = this.isLangRtl
          ? g.arabicName || g.name
          : g.name || g.arabicName;
        if (garageName) return String(garageName).trim();
      }
      return this.data?.endLocation?.name || "";
    },
    servicePrintNameDetail() {
      const s = this.data?.service;
      if (!s) return '—';
      return this.isLangRtl ? (s.arabicName || s.name) : (s.name || s.arabicName);
    },
    garageTransferPrintPayload() {
      const t = this.data || {};
      const snap = t.hajjGarageTransferPrint || t.hajjGarageTransferPrintPayload || {};
      let vehicleTypeText = this.getVehicleTypeText(t?.vehicleCriteria?.vehicleType);
      if (vehicleTypeText && this.$te(String(vehicleTypeText))) {
        vehicleTypeText = this.$t(String(vehicleTypeText));
      }
      return buildHajjGarageTransferPrintPayloadFromTrip(t, {
        isLangRtl: this.isLangRtl,
        destinationText: this.displayEndLocationName || "—",
        serviceText: snap.serviceText || this.servicePrintNameDetail,
        vehicleTypeText: vehicleTypeText || "",
      });
    },
  },
  watch: {
    id() {
      this.getData();
    },
  },
  created() {
    this.$store.dispatch("enums/GET_VEHICLE_TYPE_ENUMS");
    this.$store.dispatch("enums/GET_COLOR_ENUMS");
    this.$store.dispatch("enums/GET_RELIGION_ENUMS");
    this.$store.dispatch("enums/GET_LANGUAGE_ENUMS");
    this.$store.dispatch("enums/GET_NATIONALITY_ENUMS");
    this.$store.dispatch("enums/GET_DRIVERS_AVAILABLE_NATIONALITY_ENUMS");
    this.$store.dispatch("enums/GET_BRAND_ENUMS");
    this.$store.dispatch("enums/GET_MODEL_ENUMS");
  },
  mounted() {
    this.getData();
  },
  i18n: {
    messages: {
      en: require("@/services/i18n/locales/en/manageTrips.json"),
      ar: require("@/services/i18n/locales/ar/manageTrips.json"),
    },
  },
};
</script>

<style lang="scss" scoped>
.header-block {
  border-radius: 0.5rem;
  border: 1px solid var(--color-64);
  background: var(--color-01);
  padding: 0.5rem 0.8125rem;
  box-shadow: 0px 2px 8px 2px var(--color-37);
}

.icon-container {
  border-radius: 0.375rem;
  background: var(--color-100);
  padding: 0.15rem;
  width: 1.5rem;
  height: 1.5rem;
  display: flex;
  justify-content: center;
  align-items: center;

  img {
    width: 100%;
    height: 100%;
    object-fit: contain;
  }
}

.trip-place-container {
  border-radius: 0.5rem;
  border: 1px solid var(--color-64);
  padding: 1.25rem 1.5rem;

  .route {
    height: 0.1875rem;
    background: var(--color-99);
  }

  .point {
    top: -6px;
    border-radius: 50%;
    width: 0.9375rem;
    height: 0.9375rem;
    background-color: var(--color-80);
    border: 4px solid var(--color-89);
  }

  .point.start {
    position: absolute;
    left: 0px;
  }

  .point.end {
    position: absolute;
    right: 0px;
  }

  .point.pick-up {
    position: absolute;
  }

  .point.drop-off {
    position: absolute;
  }

  .place {
    top: 12px;
  }

  .place.start {
    flex: 0.5;
    text-align: left;
  }

  .place.end {
    flex: 0.5;
    text-align: right;
  }

  .place.pick-up {
    flex: 0.5;
    text-align: center;
  }

  .place.drop-off {
    flex: 0.5;
    text-align: center;
  }
}

.bold-text {
  font-weight: 700;
}

.assign-block {
  border-radius: 0.5rem;
  background: var(--color-84);
  padding: 0.5rem 0.75rem;

  .avtar {
    background: var(--color-01);
    border-radius: 50%;
    margin-inline-end: 0.5rem;
    width: 3.3125rem;
    height: 3.3125rem;
    display: flex;
    justify-content: center;
    align-items: center;

    img {
      width: 100%;
      height: 100%;
      object-fit: contain;
    }
  }

  .title {
    font-size: 0.875rem;
  }

  .sub-title {
    font-size: 0.75rem;
  }

  .reassign-btn {
    border-radius: 0.375rem;
    background: var(--color-85);
    padding: 0.5rem 0.625rem;
    color: var(--color-101);
    font-size: 0.75rem;
    font-weight: 700;
  }

  .remove-btn {
    padding: 0.5rem 0.625rem;
    font-size: 0.75rem;
    font-weight: 700;
    color: var(--color-01);
    border-radius: 0.375rem;
  }
}

.info-block {
  padding: 0.625rem;
  border-radius: 0.5rem;

  .icon {
    display: flex;
    padding: 0.4375rem;
    justify-content: center;
    align-items: center;
    border-radius: 50%;
  }
}

.services-block-container {
  display: flex;
  flex-wrap: wrap;
  gap: 24px;

  .services-block {
    width: 50%;

    &:nth-child(odd) {
      width: calc(50% - 24px);
    }
  }
}

.services-block {
  border-radius: 0.5rem;
  background: var(--color-01);
  padding: 0.5rem 0.75rem;
  box-shadow: 0px 12px 16px var(--color-38);
  border: 1px solid var(--color-14);

}

.show-map-options {
  position: absolute;
  top: 45px;
  width: 95%;
  z-index: 9999;
  background: var(--color-01);
  border: 1px solid var(--color-14);
  box-shadow: 0px 2px 3px var(--color-38);
  border-radius: 4px;

  .option {
    cursor: pointer;
    padding: 0.5rem;
    font-size: 12px;
    line-height: 24px;
    color: var(--color-02);
  }

  .option:hover {
    background-color: var(--color-23);
    color: var(--color-01);
  }
}

.criteria-block {
  border-radius: 0.5rem;
  border: 1px solid var(--color-84);
  padding: 0.5rem 0.75rem;

  .title {
    font-size: 0.875rem;
  }

  .sub-title {
    font-size: 0.75rem;
  }

  .reassign-btn {
    border-radius: 0.375rem;
    background: var(--color-85);
    padding: 0.5rem 0.625rem;
    color: var(--color-101);
    font-size: 0.75rem;
    font-weight: 700;
  }
}

.pdf-content {
  visibility: hidden;
}

.hajj-garage-transfer-trip-table-offscreen {
  position: fixed;
  left: -10000px;
  top: 0;
  width: 210mm;
  pointer-events: none;
  opacity: 0;
}
</style>